import { createElement, lazy, type ComponentProps, type ComponentType } from 'react';

/**
 * 다시 받을 수 있는 지연 청크 (14회차 m63 · Codex 6차 N30).
 *
 * React.lazy 는 한 번 거절된 약속을 기억하고, 크롬도 한 번 실패한 동적 import 를 모듈 표에 기억해 **같은 주소로는 네트워크에 가지도 않고**
 * 다시 실패한다. 그래서 첫 화면이 한가할 때 미리 받기(App.tsx)가 한 번 실패하면 그 탭에서 원단 상세 · 전체 원단이 모두 오류 화면으로 열렸고,
 * 오류 경계의 「다시 시도」 도 같은 실패를 다시 보여 줬다(「홈으로」 — 전체 새로 읽기 — 만 풀었다).
 *
 *  · 받기는 먼저 같은 주소, 안 되면 주소 끝에 시도 번호(?retry=)를 붙여 새로 받는다(freshLoader — 트레이 TraySlot 과 같은 방식).
 *    그래서 미리 받기만 실패했고 지금 연결이 되면 사용자는 오류를 보지 않는다.
 *  · 그래도 못 받으면 ChunkLoadError — 오류 경계가 「화면 파일을 받지 못했습니다」 로 원인을 구분해 말한다.
 *  · 실패한 청크는 목록에 남고, 오류 경계의 「다시 시도」 · 주소 바뀜이 resetFailedChunks() 로 **새 lazy 를 만든다** — 다음 그리기에서 실제로 다시 받는다.
 * 청크 주소는 로더 소스 안 import("…") 글자에서 읽는다(개발: /src/pages/Detail.tsx, 운영: ./Detail-해시.js — 이 파일은 주 번들이라 기준 주소가 같은 폴더).
 */
type Loader<T> = () => Promise<{ default: T }>;

export class ChunkLoadError extends Error {
  constructor(readonly original: unknown) {
    super('화면 파일을 받지 못했습니다');
    this.name = 'ChunkLoadError';
  }
}

/** 화면 파일(지연 청크)을 받지 못한 오류인가 — 감싼 오류 · 브라우저 원문 둘 다 */
export function isChunkError(e: unknown): boolean {
  if (e instanceof ChunkLoadError) return true;
  const m = e instanceof Error ? e.message : String(e ?? '');
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(m);
}

/**
 * 먼저 같은 주소로, 안 되면 주소 끝에 시도 번호를 붙여 새로 받는다.
 * 운영 빌드는 다른 화면과 나눠 쓰는 모듈이 든 청크를 `import("./Detail-해시.js").then(l=>l.D)` 처럼 **이름 붙은 내보내기 안의 모듈 묶음**으로
 * 꺼낸다(Rollup) — 그 `.then(x=>x.이름)` 까지 읽어 같은 것을 꺼낸다(14회차 실측: 빼먹으면 default 가 없어 React 오류 #306).
 */
export function freshLoader<T>(load: Loader<T>): Loader<T> {
  return async () => {
    try {
      return await load();
    } catch (e) {
      const m = /import\((["'`])([^"'`]+)\1\)(?:\.then\(\(?\s*(\w+)\s*\)?\s*=>\s*\3\.(\w+)\s*\))?/.exec(String(load));
      if (!m) throw e;
      const url = new URL(m[2], import.meta.url);
      url.searchParams.set('retry', String(Date.now()));
      const mod = await import(/* @vite-ignore */ url.href) as Record<string, unknown>;
      const picked = (m[4] ? mod[m[4]] : mod) as { default?: T } | undefined;
      if (!picked || picked.default === undefined) throw e;
      return picked as { default: T };
    }
  };
}

/** 받지 못한 청크들의 「새 lazy 만들기」 — 오류 경계가 다시 시도할 때 부른다 */
const failed = new Set<() => void>();

/** 실패한 청크를 모두 새 lazy 로 — 몇 개였는지 돌려준다 */
export function resetFailedChunks(): number {
  const n = failed.size;
  const xs = [...failed];
  failed.clear();
  xs.forEach((f) => f());
  return n;
}

/**
 * React.lazy 대신 — 못 받으면 다시 받을 수 있다. 그리는 쪽은 lazy 와 똑같이 쓴다(Suspense 안).
 * `preload()` 는 한가할 때 미리 받기(실패해도 조용히 — 그리기 때 다시 받는다).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRetry<T extends ComponentType<any>>(load: Loader<T>): T & { preload: () => Promise<unknown> } {
  const make = () => {
    const get = freshLoader(load);
    return lazy(() => get().catch((e) => { failed.add(reset); throw new ChunkLoadError(e); }));
  };
  let cur = make();
  function reset() { cur = make(); }
  const Wrapped = (props: ComponentProps<T>) => createElement(cur, props);
  const out = Wrapped as unknown as T & { preload: () => Promise<unknown> };
  out.preload = () => load().catch(() => undefined);
  return out;
}
